import { useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";

export const GalleryCarousel = ({ items = [], idPrefix = "gallery" }) => {
  const [index, setIndex] = useState(0);
  const dragX = useRef(null);
  const n = items.length;
  if (!n) return null;
  const go = (dir) => setIndex((i) => Math.min(Math.max(i + dir, 0), n - 1));

  return (
    <div data-testid={`${idPrefix}-carousel`}>
      <div
        className="relative h-[340px] touch-pan-y select-none overflow-hidden sm:h-[440px] lg:h-[520px]"
        onPointerDown={(e) => { dragX.current = e.clientX; }}
        onPointerUp={(e) => {
          if (dragX.current === null) return;
          const dx = e.clientX - dragX.current;
          dragX.current = null;
          if (dx <= -48) go(1);
          else if (dx >= 48) go(-1);
        }}
      >
        {items.map((img, i) => {
          const off = i - index;
          const abs = Math.abs(off);
          return (
            <motion.figure
              key={`${img.url}-${i}`}
              className="absolute left-1/2 top-0 h-full w-[82%] overflow-hidden rounded-2xl ring-1 ring-black/10 sm:w-[60%] sm:rounded-3xl lg:w-[46%]"
              style={{
                zIndex: 30 - abs,
                boxShadow: "0 30px 60px -20px rgba(28,25,23,0.35)",
                pointerEvents: abs === 0 ? "auto" : "none",
              }}
              initial={false}
              animate={{
                x: `${-50 + off * 62}%`,
                scale: abs === 0 ? 1 : abs === 1 ? 0.84 : 0.72,
                rotate: off * 2.5,
                opacity: abs > 2 ? 0 : 1,
              }}
              transition={{ duration: 0.55, ease: [0.32, 0.72, 0, 1] }}
            >
              <img src={img.url} alt={img.alt || ""} loading="lazy" draggable={false}
                className="pointer-events-none h-full w-full object-cover" />
              <motion.div className="pointer-events-none absolute inset-0 bg-ink" initial={false}
                animate={{ opacity: abs === 0 ? 0 : abs === 1 ? 0.28 : 0.5 }}
                transition={{ duration: 0.5 }} />
            </motion.figure>
          );
        })}
      </div>

      <div className="mt-6 flex items-center justify-between">
        <p data-testid={`${idPrefix}-count`} className="text-xs font-medium tracking-[0.2em] text-stone-400">
          {String(index + 1).padStart(2, "0")} — {String(n).padStart(2, "0")}
        </p>
        <div className="flex gap-3">
          <button onClick={() => go(-1)} disabled={index === 0} data-testid={`${idPrefix}-prev`}
            aria-label="Previous photos"
            className="flex h-12 w-12 items-center justify-center rounded-full border border-black/10 text-ink transition-colors duration-300 hover:bg-ink hover:text-bone disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-ink">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <button onClick={() => go(1)} disabled={index === n - 1} data-testid={`${idPrefix}-next`}
            aria-label="Next photos"
            className="flex h-12 w-12 items-center justify-center rounded-full border border-black/10 text-ink transition-colors duration-300 hover:bg-ink hover:text-bone disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-ink">
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="mt-4 flex min-h-8 items-start justify-center">
        <AnimatePresence mode="wait">
          <motion.p key={index} data-testid={`${idPrefix}-caption`}
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }}
            className="font-serif text-lg italic text-stone-600">
            {items[index].caption || ""}
          </motion.p>
        </AnimatePresence>
      </div>
    </div>
  );
};
